import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Table, Button, Modal, Form, Badge, Spinner, Alert, Pagination, Accordion } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faFileAlt, faPlus, faDownload, faEye } from '@fortawesome/free-solid-svg-icons';
import LessonPlanService from '../../services/lessonPlan.service';
import SubjectService from '../../services/subject.service';
import GradeService from '../../services/grade.service';
import CompetenceService from '../../services/competence.service';
import SkillService from '../../services/skill.service';
import { useAuth } from '../../contexts/AuthContext';
import { useAcademicYear } from '../../contexts/AcademicYearContext';
import { useToast } from '../../contexts/ToastContext';

const LessonPlans = () => {
  const { user } = useAuth();
  const { selectedYear } = useAcademicYear();
  const { showToast } = useToast();

  const [lessonPlans, setLessonPlans] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    currentPage: 1,
    totalPages: 1
  });

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createStep, setCreateStep] = useState(1);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showCreateCompetenceModal, setShowCreateCompetenceModal] = useState(false);
  const [showCreateSkillModal, setShowCreateSkillModal] = useState(false);
  const [selectedLessonPlan, setSelectedLessonPlan] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [availableCompetences, setAvailableCompetences] = useState([]);
  const [availableSkills, setAvailableSkills] = useState([]);

  const [newCompetence, setNewCompetence] = useState({
    code: '',
    description: ''
  });

  const [newSkill, setNewSkill] = useState({
    competence: '',
    code: '',
    description: ''
  });

  // Multi-step creation states
  const [competencesList, setCompetencesList] = useState([]);
  const [skillsList, setSkillsList] = useState([]);
  const [editingCompetenceIndex, setEditingCompetenceIndex] = useState(null);
  const [editingSkillIndex, setEditingSkillIndex] = useState(null);

  const [formData, setFormData] = useState({
    subject: '',
    grade: '',
    year: selectedYear,
    title: '',
    workload: 0,
    file: null,
    competences: [],
    skills: []
  });

  const [filters, setFilters] = useState({
    subject: '',
    grade: '',
    search: ''
  });

  useEffect(() => {
    fetchGrades();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user) {
      fetchSubjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    fetchLessonPlans(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, filters]);

  const fetchGrades = async () => {
    try {
      const data = await GradeService.getAll();
      setGrades(data.results || []);
    } catch (err) {
      console.error('Error fetching grades:', err);
    }
  };

  const fetchSubjects = async () => {
    try {
      const activeSchool = user?.school_users?.find(su => su.active);

      const data = await SubjectService.getAll({ school: activeSchool.school.id });
      const subjectsList = data.results || data || [];
      setSubjects(subjectsList);
    } catch (err) {
      console.error('Error fetching subjects:', err);
    }
  };

  const fetchLessonPlans = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const params = { year: selectedYear, school: user?.school_users?.find(su => su.active).school.id, page };

      if (filters.subject) params.subject = filters.subject;
      if (filters.grade) params.grade = filters.grade;
      if (filters.search) params.search = filters.search;

      const data = await LessonPlanService.getAll(params);
      setLessonPlans(data.results || []);
      setPagination({
        count: data.count || 0,
        next: data.next,
        previous: data.previous,
        currentPage: page,
        totalPages: Math.ceil((data.count || 0) / 10)
      });
    } catch (err) {
      setError('Erro ao carregar planos de aula. Tente novamente.');
      console.error('Error fetching lesson plans:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    fetchLessonPlans(page);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      subject: '',
      grade: '',
      search: ''
    });
  };

  const fetchCompetencesBySubject = async (subjectId) => {
    try {
      const data = await CompetenceService.getAll({ subject: subjectId });
      const competencesList = data.results || data || [];
      setAvailableCompetences(competencesList);
      return competencesList;
    } catch (err) {
      console.error('Error fetching competences:', err);
      return [];
    }
  };

  const fetchSkillsByCompetences = async (competenceIds) => {
    try {
      if (!competenceIds || competenceIds.length === 0) {
        setAvailableSkills([]);
        return [];
      }

      const allSkills = [];
      for (const compId of competenceIds) {
        const data = await SkillService.getAll({ competence: compId });
        const skills = data.results || data || [];
        allSkills.push(...skills);
      }
      setAvailableSkills(allSkills);
      return allSkills;
    } catch (err) {
      console.error('Error fetching skills:', err);
      return [];
    }
  };

  const handleCreateCompetence = async () => {
    try {
      if (!newCompetence.code || !formData.subject) {
        showToast('Preencha o código da competência.', 'warning');
        return;
      }

      const payload = {
        subject: formData.subject,
        code: newCompetence.code,
        description: newCompetence.description
      };

      const created = await CompetenceService.create(payload);
      showToast('Competência criada com sucesso!', 'success');

      // Atualizar lista de competências
      await fetchCompetencesBySubject(formData.subject);

      // Adicionar automaticamente às competências selecionadas
      setFormData(prev => ({
        ...prev,
        competences: [...prev.competences, created.id]
      }));

      // Limpar formulário e fechar modal
      setNewCompetence({ code: '', description: '' });
      setShowCreateCompetenceModal(false);
    } catch (err) {
      console.error('Error creating competence:', err);
      showToast('Erro ao criar competência.', 'error');
    }
  };

  const handleCreateSkill = async () => {
    try {
      if (!newSkill.code || !newSkill.competence) {
        showToast('Preencha o código da habilidade e selecione uma competência.', 'warning');
        return;
      }

      const payload = {
        competence: newSkill.competence,
        code: newSkill.code,
        description: newSkill.description
      };

      const created = await SkillService.create(payload);
      showToast('Habilidade criada com sucesso!', 'success');

      // Atualizar lista de habilidades
      await fetchSkillsByCompetences(formData.competences);

      // Adicionar automaticamente às habilidades selecionadas
      setFormData(prev => ({
        ...prev,
        skills: [...prev.skills, created.id]
      }));

      // Limpar formulário e fechar modal
      setNewSkill({ competence: '', code: '', description: '' });
      setShowCreateSkillModal(false);
    } catch (err) {
      console.error('Error creating skill:', err);
      showToast('Erro ao criar habilidade.', 'error');
    }
  };

  const handleOpenCreateModal = () => {
    setFormData({
      subject: '',
      grade: '',
      year: selectedYear,
      title: '',
      file: null,
      competences: [],
      skills: []
    });
    setSelectedFile(null);
    setShowCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setCreateStep(1);
    setSelectedFile(null);
    setCompetencesList([]);
    setSkillsList([]);
    setNewCompetence({ code: '', description: '' });
    setNewSkill({ competence: '', code: '', description: '' });
    setEditingCompetenceIndex(null);
    setEditingSkillIndex(null);
  };

  const handleView = async (lessonPlan) => {
    setSelectedLessonPlan(lessonPlan);
    setShowViewModal(true);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setSelectedLessonPlan(null);
  };

  const handleEdit = async (lessonPlan) => {
    setSelectedLessonPlan(lessonPlan);
    setFormData({
      subject: lessonPlan.subject,
      grade: lessonPlan.grade,
      year: lessonPlan.year,
      title: lessonPlan.title,
      workload: lessonPlan.workload || 0,
      file: null,
      competences: lessonPlan.competences || [],
      skills: lessonPlan.skills || []
    });
    setSelectedFile(null);

    // Carregar competências e habilidades da disciplina
    if (lessonPlan.subject) {
      await fetchCompetencesBySubject(lessonPlan.subject);
      if (lessonPlan.competences && lessonPlan.competences.length > 0) {
        await fetchSkillsByCompetences(lessonPlan.competences);
      }
    }

    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedLessonPlan(null);
    setSelectedFile(null);
  };

  const handleInputChange = async (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Quando a disciplina for alterada, carregar competências
    if (name === 'subject' && value) {
      await fetchCompetencesBySubject(value);
      // Limpar competências e habilidades selecionadas
      setFormData(prev => ({
        ...prev,
        competences: [],
        skills: []
      }));
      setAvailableSkills([]);
    }
  };

  const handleCompetenceToggle = (competenceId) => {
    setFormData(prev => {
      const isSelected = prev.competences.includes(competenceId);
      const newCompetences = isSelected
        ? prev.competences.filter(id => id !== competenceId)
        : [...prev.competences, competenceId];

      // Atualizar habilidades disponíveis
      fetchSkillsByCompetences(newCompetences);

      return {
        ...prev,
        competences: newCompetences,
        // Limpar habilidades se a competência for desmarcada
        skills: isSelected
          ? prev.skills.filter(skillId => {
              const skill = availableSkills.find(s => s.id === skillId);
              return skill && newCompetences.includes(skill.competence);
            })
          : prev.skills
      };
    });
  };

  const handleSkillToggle = (skillId) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.includes(skillId)
        ? prev.skills.filter(id => id !== skillId)
        : [...prev.skills, skillId]
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  // Multi-step creation functions
  const handleNextStep = () => {
    if (createStep === 1) {
      // Validar campos obrigatórios da etapa 1
      if (!formData.subject || !formData.grade || !formData.title) {
        showToast('Preencha todos os campos obrigatórios.', 'warning');
        return;
      }
    }
    setCreateStep(prev => prev + 1);
  };

  const handlePreviousStep = () => {
    setCreateStep(prev => prev - 1);
  };

  const handleAddCompetence = () => {
    if (!newCompetence.code.trim()) {
      showToast('Código da competência é obrigatório.', 'warning');
      return;
    }

    if (editingCompetenceIndex !== null) {
      // Editar competência existente
      const updated = [...competencesList];
      updated[editingCompetenceIndex] = { ...newCompetence };
      setCompetencesList(updated);
      setEditingCompetenceIndex(null);
    } else {
      // Adicionar nova competência
      setCompetencesList(prev => [...prev, { ...newCompetence }]);
    }

    setNewCompetence({ code: '', description: '' });
  };

  const handleEditCompetence = (index) => {
    setNewCompetence({ ...competencesList[index] });
    setEditingCompetenceIndex(index);
  };

  const handleRemoveCompetence = (index) => {
    setCompetencesList(prev => prev.filter((_, i) => i !== index));
    // Remover habilidades vinculadas a esta competência
    setSkillsList(prev => prev.filter(skill => skill.competenceIndex !== index));
  };

  const handleAddSkill = () => {
    if (!newSkill.code.trim()) {
      showToast('Código da habilidade é obrigatório.', 'warning');
      return;
    }

    if (!newSkill.competence && competencesList.length > 0) {
      showToast('Selecione uma competência para a habilidade.', 'warning');
      return;
    }

    if (editingSkillIndex !== null) {
      // Editar habilidade existente
      const updated = [...skillsList];
      updated[editingSkillIndex] = { ...newSkill };
      setSkillsList(updated);
      setEditingSkillIndex(null);
    } else {
      // Adicionar nova habilidade
      setSkillsList(prev => [...prev, { ...newSkill }]);
    }

    setNewSkill({ competence: '', code: '', description: '' });
  };

  const handleEditSkill = (index) => {
    setNewSkill({ ...skillsList[index] });
    setEditingSkillIndex(index);
  };

  const handleRemoveSkill = (index) => {
    setSkillsList(prev => prev.filter((_, i) => i !== index));
  };

  const handleCreate = async () => {
    try {
      const activeSchool = user?.school_users?.find(su => su.active);
      if (!activeSchool) {
        showToast('Nenhuma escola ativa encontrada.', 'warning');
        return;
      }

      // Validar campos obrigatórios
      if (!formData.subject || !formData.grade || !formData.title) {
        showToast('Preencha todos os campos obrigatórios.', 'warning');
        return;
      }

      // Preparar payload para criação em lote
      const payload = {
        school: activeSchool.school.id,
        teacher: user.id,
        subject: formData.subject,
        grade: formData.grade,
        year: Number(formData.year),
        title: formData.title,
        workload: Number(formData.workload) || 0,
        competences_data: competencesList.map(comp => ({
          code: comp.code,
          description: comp.description || ''
        })),
        skills_data: skillsList.map(skill => ({
          competence_index: parseInt(skill.competence),
          code: skill.code,
          description: skill.description || ''
        }))
      };

      // Converter arquivo para base64 se houver
      if (selectedFile) {
        const base64File = await LessonPlanService.fileToBase64(selectedFile);
        payload.file = base64File;
      }

      // Criar plano de aula com competências e habilidades em uma única requisição
      await LessonPlanService.batchCreate(payload);
      await fetchLessonPlans(pagination.currentPage);
      handleCloseCreateModal();
      showToast('Plano de aula criado com sucesso!', 'success');
    } catch (err) {
      console.error('Error creating lesson plan:', err);
      showToast('Erro ao criar plano de aula. Tente novamente.', 'danger');
    }
  };

  const handleSave = async () => {
    try {
      // Validar campos obrigatórios
      if (!formData.subject || !formData.grade || !formData.title) {
        showToast('Preencha todos os campos obrigatórios.', 'warning');
        return;
      }

      const activeSchool = user?.school_users?.find(su => su.active);

      const payload = {
        school: activeSchool?.school?.id || selectedLessonPlan.school,
        teacher: user?.id || selectedLessonPlan.teacher,
        subject: formData.subject,
        grade: formData.grade,
        year: Number(formData.year),
        title: formData.title,
        workload: Number(formData.workload) || 0,
        competences: formData.competences,
        skills: formData.skills
      };

      // Converter arquivo para base64 se houver
      if (selectedFile) {
        const base64File = await LessonPlanService.fileToBase64(selectedFile);
        payload.file = base64File;
      }

      await LessonPlanService.partialUpdate(selectedLessonPlan.id, payload);
      await fetchLessonPlans(pagination.currentPage);
      handleCloseEditModal();
      showToast('Plano de aula atualizado com sucesso!', 'success');
    } catch (err) {
      console.error('Error updating lesson plan:', err);
      showToast('Erro ao atualizar plano de aula. Tente novamente.', 'danger');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este plano de aula?')) {
      try {
        await LessonPlanService.delete(id);
        await fetchLessonPlans(pagination.currentPage);
        showToast('Plano de aula excluído com sucesso!', 'success');
      } catch (err) {
        console.error('Error deleting lesson plan:', err);
        showToast('Erro ao excluir plano de aula. Tente novamente.', 'danger');
      }
    }
  };

  const getEducationLevelLabel = (level) => {
    const levels = {
      'educacao_infantil': 'Educação Infantil',
      'ensino_fundamental_i': 'Ensino Fundamental I',
      'ensino_fundamental_ii': 'Ensino Fundamental II',
      'ensino_medio': 'Ensino Médio'
    };
    return levels[level] || level;
  };

  const getGradeName = (gradeId) => {
    const grade = grades.find(g => g.id === gradeId);
    return grade ? `${grade.name}${grade.education_level ? ` - ${getEducationLevelLabel(grade.education_level)}` : ''}` : 'N/A';
  };

  const getSubjectName = (subjectId) => {
    const subject = subjects.find(s => s.id === subjectId);
    return subject ? subject.name : 'N/A';
  };

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <h4>Planos de Aula - {selectedYear}</h4>
          <p className="mb-0">Gerencie os planos de aula da escola</p>
        </div>
      </div>

      <Card border="light" className="shadow-sm mb-4">
        <Card.Body>
          <Row className="mb-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Disciplina</Form.Label>
                <Form.Select
                  name="subject"
                  value={filters.subject}
                  onChange={handleFilterChange}
                >
                  <option value="">Todas as disciplinas</option>
                  {subjects.map((subject) => (
                    <option key={subject.id} value={subject.id}>
                      {subject.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Série</Form.Label>
                <Form.Select
                  name="grade"
                  value={filters.grade}
                  onChange={handleFilterChange}
                >
                  <option value="">Todas as séries</option>
                  {grades.map((grade) => (
                    <option key={grade.id} value={grade.id}>
                      {grade.education_level ? `${grade.name} - ${getEducationLevelLabel(grade.education_level)}` : grade.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Buscar</Form.Label>
                <Form.Control
                  type="text"
                  name="search"
                  placeholder="Digite o título..."
                  value={filters.search}
                  onChange={handleFilterChange}
                />
              </Form.Group>
            </Col>
            <Col md={3} className="d-flex align-items-end">
              <Button
                variant="secondary"
                onClick={handleClearFilters}
                className="w-100"
              >
                Limpar Filtros
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Row>
        <Col xs={12} className="mb-4">
          <Card border="light" className="shadow-sm">
            <Card.Header>
              <Row className="align-items-center">
                <Col>
                  <h5 className="mb-0">
                    <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                    Lista de Planos de Aula
                  </h5>
                </Col>
                <Col className="text-end">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleOpenCreateModal}
                  >
                    <FontAwesomeIcon icon={faPlus} className="me-1" />
                    Novo Plano de Aula
                  </Button>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body className="p-0">
              {loading && (
                <div className="text-center py-5">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </Spinner>
                </div>
              )}
              {error && (
                <Alert variant="danger" className="m-3">
                  {error}
                </Alert>
              )}
              {!loading && !error && lessonPlans.length === 0 && (
                <div className="text-center py-5">
                  <p className="text-muted">Nenhum plano de aula encontrado.</p>
                </div>
              )}
              {!loading && !error && lessonPlans.length > 0 && (
                <Table responsive hover className="align-items-center table-flush mb-0">
                  <thead className="thead-light">
                    <tr>
                      <th>Título</th>
                      <th>Disciplina</th>
                      <th>Série</th>
                      <th>Ano</th>
                      <th>Carga Horária</th>
                      <th>Arquivo</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lessonPlans.map((plan) => (
                      <tr key={plan.id}>
                        <td className="fw-bold">{plan.title}</td>
                        <td>{getSubjectName(plan.subject)}</td>
                        <td>{getGradeName(plan.grade)}</td>
                        <td>{plan.year}</td>
                        <td>{plan.workload || 0} aula(s)</td>
                        <td>
                          {plan.file ? (
                            <a href={plan.file} target="_blank" rel="noopener noreferrer">
                              <Badge bg="success">
                                <FontAwesomeIcon icon={faDownload} className="me-1" />
                                Download
                              </Badge>
                            </a>
                          ) : (
                            <Badge bg="secondary">Sem arquivo</Badge>
                          )}
                        </td>
                        <td className="text-center">
                          <Button
                            variant="info"
                            size="sm"
                            className="me-2"
                            onClick={() => handleView(plan)}
                          >
                            <FontAwesomeIcon icon={faEye} />
                          </Button>
                          <Button
                            variant="warning"
                            size="sm"
                            className="me-2"
                            onClick={() => handleEdit(plan)}
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleDelete(plan.id)}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
            {lessonPlans.length > 0 && (
              <Card.Footer className="px-3 border-0 d-flex justify-content-center">
                <Pagination className="mb-0">
                  <Pagination.Prev
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={!pagination.previous}
                  />
                  {[...Array(pagination.totalPages)].map((_, index) => (
                    <Pagination.Item
                      key={index + 1}
                      active={index + 1 === pagination.currentPage}
                      onClick={() => handlePageChange(index + 1)}
                    >
                      {index + 1}
                    </Pagination.Item>
                  ))}
                  <Pagination.Next
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={!pagination.next}
                  />
                </Pagination>
              </Card.Footer>
            )}
          </Card>
        </Col>
      </Row>

      <Modal show={showCreateModal} onHide={handleCloseCreateModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Novo Plano de Aula - Etapa {createStep} de 3
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {createStep === 1 && (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Título *</Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Digite o título do plano de aula"
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Disciplina *</Form.Label>
                    <Form.Select
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                    >
                      <option value="">Selecione a disciplina</option>
                      {subjects.map((subject) => (
                        <option key={subject.id} value={subject.id}>
                          {subject.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Série *</Form.Label>
                    <Form.Select
                      name="grade"
                      value={formData.grade}
                      onChange={handleInputChange}
                    >
                      <option value="">Selecione a série</option>
                      {grades.map((grade) => (
                        <option key={grade.id} value={grade.id}>
                          {grade.education_level ? `${grade.name} - ${getEducationLevelLabel(grade.education_level)}` : grade.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Ano Letivo *</Form.Label>
                    <Form.Control
                      type="number"
                      name="year"
                      value={formData.year}
                      onChange={handleInputChange}
                      placeholder="Ex: 2024"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Carga Horária *</Form.Label>
                    <Form.Control
                      type="number"
                      name="workload"
                      value={formData.workload}
                      onChange={handleInputChange}
                      placeholder="Número de aulas"
                      min="0"
                    />
                    <Form.Text className="text-muted">
                      Número de aulas desta disciplina
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Arquivo do Plano de Aula</Form.Label>
                <Form.Control
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileChange}
                />
                <Form.Text className="text-muted">
                  Formatos aceitos: PDF, DOC, DOCX
                </Form.Text>
              </Form.Group>
            </Form>
          )}

          {/* Etapa 2: Criar Competências */}
          {createStep === 2 && (
            <>
              <Alert variant="info">
                <strong>Etapa 2:</strong> Adicione as competências que serão trabalhadas neste plano de aula. Você pode adicionar várias competências.
              </Alert>

              <Form className="mb-3">
                <Form.Group className="mb-3">
                  <Form.Label>Código da Competência *</Form.Label>
                  <Form.Control
                    type="text"
                    value={newCompetence.code}
                    onChange={(e) => {
                      const value = e.target.value;
                      setNewCompetence(prev => ({ ...prev, code: value }));
                    }}
                    placeholder="Ex: EF01MA01"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Descrição da Competência</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={newCompetence.description}
                    onChange={(e) => {
                      const value = e.target.value;
                      setNewCompetence(prev => ({ ...prev, description: value }));
                    }}
                    placeholder="Descreva a competência detalhadamente..."
                  />
                </Form.Group>

                <Button
                  variant={editingCompetenceIndex !== null ? "warning" : "primary"}
                  onClick={handleAddCompetence}
                  className="w-100"
                >
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  {editingCompetenceIndex !== null ? 'Atualizar Competência' : 'Adicionar Competência'}
                </Button>
              </Form>

              {competencesList.length > 0 && (
                <>
                  <hr />
                  <h6 className="mb-3">Competências Adicionadas ({competencesList.length})</h6>
                  <div className="list-group">
                    {competencesList.map((comp, index) => (
                      <div key={index} className="list-group-item">
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="flex-grow-1">
                            <h6 className="mb-1">
                              <Badge bg="primary" className="me-2">{comp.code}</Badge>
                            </h6>
                            <p className="mb-0 text-muted small">
                              {comp.description || 'Sem descrição'}
                            </p>
                          </div>
                          <div className="btn-group btn-group-sm">
                            <Button
                              variant="outline-warning"
                              size="sm"
                              onClick={() => handleEditCompetence(index)}
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleRemoveCompetence(index)}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {competencesList.length === 0 && (
                <Alert variant="warning" className="mt-3">
                  Nenhuma competência adicionada ainda. Adicione pelo menos uma competência para continuar.
                </Alert>
              )}
            </>
          )}

          {/* Etapa 3: Criar Habilidades */}
          {createStep === 3 && (
            <>
              <Alert variant="info">
                <strong>Etapa 3:</strong> Adicione as habilidades relacionadas às competências. Você pode adicionar várias habilidades.
              </Alert>

              {competencesList.length === 0 ? (
                <Alert variant="warning">
                  Você precisa adicionar competências antes de criar habilidades.
                </Alert>
              ) : (
                <>
                  <Form className="mb-3">
                    <Form.Group className="mb-3">
                      <Form.Label>Competência *</Form.Label>
                      <Form.Select
                        value={newSkill.competence}
                        onChange={(e) => {
                          const value = e.target.value;
                          setNewSkill(prev => ({ ...prev, competence: value }));
                        }}
                      >
                        <option value="">Selecione a competência</option>
                        {competencesList.map((comp, index) => (
                          <option key={index} value={index}>
                            {comp.code} - {comp.description?.substring(0, 50) || 'Sem descrição'}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Código da Habilidade *</Form.Label>
                      <Form.Control
                        type="text"
                        value={newSkill.code}
                        onChange={(e) => {
                          const value = e.target.value;
                          setNewSkill(prev => ({ ...prev, code: value }));
                        }}
                        placeholder="Ex: EF01MA01A"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Descrição da Habilidade</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        value={newSkill.description}
                        onChange={(e) => {
                          const value = e.target.value;
                          setNewSkill(prev => ({ ...prev, description: value }));
                        }}
                        placeholder="Descreva a habilidade detalhadamente..."
                      />
                    </Form.Group>

                    <Button
                      variant={editingSkillIndex !== null ? "warning" : "success"}
                      onClick={handleAddSkill}
                      className="w-100"
                    >
                      <FontAwesomeIcon icon={faPlus} className="me-2" />
                      {editingSkillIndex !== null ? 'Atualizar Habilidade' : 'Adicionar Habilidade'}
                    </Button>
                  </Form>

                  {skillsList.length > 0 && (
                    <>
                      <hr />
                      <h6 className="mb-3">Habilidades Adicionadas ({skillsList.length})</h6>
                      <div className="list-group">
                        {skillsList.map((skill, index) => {
                          const relatedComp = competencesList[parseInt(skill.competence)];
                          return (
                            <div key={index} className="list-group-item">
                              <div className="d-flex justify-content-between align-items-start">
                                <div className="flex-grow-1">
                                  <h6 className="mb-1">
                                    <Badge bg="success" className="me-2">{skill.code}</Badge>
                                    <small className="text-muted">
                                      → {relatedComp?.code}
                                    </small>
                                  </h6>
                                  <p className="mb-0 text-muted small">
                                    {skill.description || 'Sem descrição'}
                                  </p>
                                </div>
                                <div className="btn-group btn-group-sm">
                                  <Button
                                    variant="outline-warning"
                                    size="sm"
                                    onClick={() => handleEditSkill(index)}
                                  >
                                    <FontAwesomeIcon icon={faEdit} />
                                  </Button>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => handleRemoveSkill(index)}
                                  >
                                    <FontAwesomeIcon icon={faTrash} />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseCreateModal}>
            Cancelar
          </Button>

          {createStep > 1 && (
            <Button variant="outline-primary" onClick={handlePreviousStep}>
              Anterior
            </Button>
          )}

          {createStep < 3 ? (
            <Button
              variant="primary"
              onClick={handleNextStep}
              disabled={createStep === 2 && competencesList.length === 0}
            >
              Próximo
            </Button>
          ) : (
            <Button variant="success" onClick={handleCreate}>
              <FontAwesomeIcon icon={faPlus} className="me-2" />
              Criar Plano de Aula
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <Modal show={showEditModal} onHide={handleCloseEditModal} backdrop="static" centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Editar Plano de Aula</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Título *</Form.Label>
              <Form.Control
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Digite o título do plano de aula"
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Disciplina *</Form.Label>
                  <Form.Select
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                  >
                    <option value="">Selecione a disciplina</option>
                    {subjects.map((subject) => (
                      <option key={subject.id} value={subject.id}>
                        {subject.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Série *</Form.Label>
                  <Form.Select
                    name="grade"
                    value={formData.grade}
                    onChange={handleInputChange}
                  >
                    <option value="">Selecione a série</option>
                    {grades.map((grade) => (
                      <option key={grade.id} value={grade.id}>
                        {grade.education_level ? `${grade.name} - ${getEducationLevelLabel(grade.education_level)}` : grade.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Ano Letivo *</Form.Label>
                  <Form.Control
                    type="number"
                    name="year"
                    value={formData.year}
                    onChange={handleInputChange}
                    placeholder="Ex: 2024"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Carga Horária *</Form.Label>
                  <Form.Control
                    type="number"
                    name="workload"
                    value={formData.workload}
                    onChange={handleInputChange}
                    placeholder="Número de aulas"
                    min="0"
                  />
                  <Form.Text className="text-muted">
                    Número de aulas desta disciplina
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Arquivo do Plano de Aula</Form.Label>
              <Form.Control
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={handleFileChange}
              />
              <Form.Text className="text-muted">
                {selectedLessonPlan?.file ? 'Deixe em branco para manter o arquivo atual' : 'Formatos aceitos: PDF, DOC, DOCX'}
              </Form.Text>
            </Form.Group>

            {/* Competências e Habilidades */}
            {formData.subject && (
              <>
                <hr />
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0">Competências</h6>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => setShowCreateCompetenceModal(true)}
                    >
                      <FontAwesomeIcon icon={faPlus} className="me-1" />
                      Nova Competência
                    </Button>
                  </div>
                  {availableCompetences.length === 0 ? (
                    <Alert variant="info" className="mb-0">
                      Nenhuma competência cadastrada para esta disciplina.
                    </Alert>
                  ) : (
                    <div className="d-flex flex-wrap gap-2">
                      {availableCompetences.map((comp) => {
                        const description = comp.description || 'Sem descrição';
                        const truncatedDesc = description.length > 15
                          ? description.substring(0, 15) + '...'
                          : description;
                        return (
                          <Badge
                            key={comp.id}
                            bg="primary"
                            className="p-2"
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleCompetenceToggle(comp.id)}
                            title={comp.description}
                          >
                            {comp.code} - {truncatedDesc}
                          </Badge>
                        );
                      })}
                    </div>
                  )}
                </div>

                {formData.competences.length > 0 && (
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0">Habilidades</h6>
                      <Button
                        variant="outline-success"
                        size="sm"
                        onClick={() => {
                          setNewSkill(prev => ({ ...prev, competence: formData.competences[0] || '' }));
                          setShowCreateSkillModal(true);
                        }}
                      >
                        <FontAwesomeIcon icon={faPlus} className="me-1" />
                        Nova Habilidade
                      </Button>
                    </div>
                    {availableSkills.length === 0 ? (
                      <Alert variant="info" className="mb-0">
                        Nenhuma habilidade cadastrada para as competências selecionadas.
                      </Alert>
                    ) : (
                      <div className="d-flex flex-wrap gap-2">
                        {availableSkills.map((skill) => {
                          const description = skill.description || 'Sem descrição';
                          const truncatedDesc = description.length > 15
                            ? description.substring(0, 15) + '...'
                            : description;
                          return (
                            <Badge
                              key={skill.id}
                              bg="success"
                              className="p-2"
                              style={{ cursor: 'pointer' }}
                              onClick={() => handleSkillToggle(skill.id)}
                              title={skill.description}
                            >
                              {skill.code} - {truncatedDesc}
                            </Badge>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseEditModal}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Salvar Alterações
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showViewModal} onHide={handleCloseViewModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Visualizar Plano de Aula</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {selectedLessonPlan && (
            <>
              <div className="mb-3">
                <h6 className="text-muted mb-3">Informações do Plano</h6>
                <Row>
                  <Col md={12} className="mb-2">
                    <strong>Título:</strong> {selectedLessonPlan.title}
                  </Col>
                  <Col md={6} className="mb-2">
                    <strong>Disciplina:</strong> {getSubjectName(selectedLessonPlan.subject)}
                  </Col>
                  <Col md={6} className="mb-2">
                    <strong>Série:</strong> {getGradeName(selectedLessonPlan.grade)}
                  </Col>
                  <Col md={6} className="mb-2">
                    <strong>Ano Letivo:</strong> {selectedLessonPlan.year}
                  </Col>
                  <Col md={6} className="mb-2">
                    <strong>Carga Horária:</strong> {selectedLessonPlan.workload || 0} aula(s)
                  </Col>
                  <Col md={6} className="mb-2">
                    <strong>Arquivo:</strong>{' '}
                    {selectedLessonPlan.file ? (
                      <a href={selectedLessonPlan.file} target="_blank" rel="noopener noreferrer">
                        <Badge bg="success">
                          <FontAwesomeIcon icon={faDownload} className="me-1" />
                          Download
                        </Badge>
                      </a>
                    ) : (
                      <Badge bg="secondary">Sem arquivo</Badge>
                    )}
                  </Col>
                </Row>
              </div>

              <hr />

              <div className="mb-3">
                <h6 className="text-muted mb-3">Competências e Habilidades</h6>
                {selectedLessonPlan.competences_details && selectedLessonPlan.competences_details.length > 0 ? (
                  <Accordion defaultActiveKey="0">
                    {selectedLessonPlan.competences_details.map((comp, index) => {
                      // Filtrar habilidades desta competência
                      const competenceSkills = selectedLessonPlan.skills_details?.filter(
                        skill => skill.competence === comp.id
                      ) || [];

                      return (
                        <Accordion.Item eventKey={String(index)} key={comp.id}>
                          <Accordion.Header>
                            <div className="d-flex align-items-center w-100">
                              <Badge bg="primary" className="me-2">{comp.code}</Badge>
                              <div className="flex-grow-1">
                                <strong>{comp.description || 'Sem descrição'}</strong>
                              </div>
                              {competenceSkills.length > 0 && (
                                <Badge bg="success" className="ms-2">
                                  {competenceSkills.length} habilidade(s)
                                </Badge>
                              )}
                            </div>
                          </Accordion.Header>
                          <Accordion.Body>
                            {competenceSkills.length > 0 ? (
                              <div className="list-group">
                                {competenceSkills.map((skill) => (
                                  <div key={skill.id} className="list-group-item">
                                    <div className="d-flex align-items-start">
                                      <Badge bg="success" className="me-2">{skill.code}</Badge>
                                      <div className="flex-grow-1">
                                        <p className="mb-0">{skill.description || 'Sem descrição'}</p>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <Alert variant="info" className="mb-0">
                                Nenhuma habilidade vinculada a esta competência.
                              </Alert>
                            )}
                          </Accordion.Body>
                        </Accordion.Item>
                      );
                    })}
                  </Accordion>
                ) : (
                  <Alert variant="info" className="mb-0">
                    Nenhuma competência vinculada a este plano.
                  </Alert>
                )}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseViewModal}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showCreateCompetenceModal} onHide={() => setShowCreateCompetenceModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Nova Competência</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Código *</Form.Label>
              <Form.Control
                type="text"
                value={newCompetence.code}
                onChange={(e) => {
                  const value = e.target.value;
                  setNewCompetence(prev => ({ ...prev, code: value }));
                }}
                placeholder="Ex: EF01MA01"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Descrição</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={newCompetence.description}
                onChange={(e) => {
                  const value = e.target.value;
                  setNewCompetence(prev => ({ ...prev, description: value }));
                }}
                placeholder="Descrição da competência..."
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateCompetenceModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleCreateCompetence}>
            Criar Competência
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showCreateSkillModal} onHide={() => setShowCreateSkillModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Nova Habilidade</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Competência *</Form.Label>
              <Form.Select
                value={newSkill.competence}
                onChange={(e) => {
                  const value = e.target.value;
                  setNewSkill(prev => ({ ...prev, competence: value }));
                }}
              >
                <option value="">Selecione a competência</option>
                {availableCompetences.map((comp) => (
                  <option key={comp.id} value={comp.id}>
                    {comp.code} - {comp.description || 'Sem descrição'}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Código *</Form.Label>
              <Form.Control
                type="text"
                value={newSkill.code}
                onChange={(e) => {
                  const value = e.target.value;
                  setNewSkill(prev => ({ ...prev, code: value }));
                }}
                placeholder="Ex: EF01MA01A"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Descrição</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={newSkill.description}
                onChange={(e) => {
                  const value = e.target.value;
                  setNewSkill(prev => ({ ...prev, description: value }));
                }}
                placeholder="Descrição da habilidade..."
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateSkillModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleCreateSkill}>
            Criar Habilidade
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default LessonPlans;
